import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  Col,
  Descriptions,
  Input,
  Row,
  Spin,
  Table,
  Tag,
  Timeline,
  Typography,
  message,
} from 'antd';
import { ArrowLeftOutlined, SendOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { getQuote, sendQuote, rejectQuote, createQuoteMessage } from '../services/supplierService';
import { Quote, QuoteItem, QuoteStatus } from '../models/Quote';
import { QUOTE_STATUS_COLORS, getQuoteStatusLabel, formatAmount } from '../utils/quoteUtils';

const { Text } = Typography;

const RequestDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [quote, setQuote] = useState<Quote | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  const fetchQuote = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await getQuote(id);
      setQuote(response.quote);
    } catch (error) {
      message.error(t('loadError'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    fetchQuote();
  }, [fetchQuote]);

  const canActOnQuote =
    !!quote &&
    (quote.status === QuoteStatus.PendingServiceProvider ||
      quote.status === QuoteStatus.CustomerRejected);

  const handleSend = async () => {
    if (!id) return;
    setActionLoading(true);
    try {
      await sendQuote(id);
      message.success(t('quoteSentSuccess'));
      await fetchQuote();
    } catch (error) {
      message.error(t('operationError'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!id) return;
    setActionLoading(true);
    try {
      await rejectQuote(id);
      message.success(t('quoteRejectedSuccess'));
      await fetchQuote();
    } catch (error) {
      message.error(t('operationError'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!id || !messageText.trim()) return;
    setSendingMessage(true);
    try {
      await createQuoteMessage(id, messageText.trim());
      setMessageText('');
      message.success(t('messageSentSuccess'));
      await fetchQuote();
    } catch (error) {
      message.error(t('operationError'));
    } finally {
      setSendingMessage(false);
    }
  };

  const currency = quote?.draft_order?.currency_code;

  const itemColumns: ColumnsType<QuoteItem> = [
    {
      title: t('description'),
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: t('quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'right',
    },
    {
      title: t('unitPrice'),
      dataIndex: 'unit_price',
      key: 'unit_price',
      align: 'right',
      render: (price: number) => formatAmount(price, currency),
    },
    {
      title: t('total'),
      dataIndex: 'total',
      key: 'total',
      align: 'right',
      render: (total: number) => formatAmount(total, currency),
    },
  ];

  const sortedMessages = [...(quote?.messages || [])].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );

  const quoteNumber = quote?.quote_number ?? quote?.draft_order?.display_id;

  return (
    <div style={{ padding: 24 }}>
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/requests')}
        style={{ marginBottom: 16 }}
      >
        {t('back')}
      </Button>

      <Spin spinning={loading}>
        {quote && (
          <>
            <Card style={{ marginBottom: 16 }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 16,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: '#334E3B' }}>
                    {t('quoteDetail')} {quoteNumber ? `#${quoteNumber}` : ''}
                  </h2>
                  <Tag color={QUOTE_STATUS_COLORS[quote.status]}>
                    {getQuoteStatusLabel(quote.status, t)}
                  </Tag>
                </div>
                {canActOnQuote && (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button danger onClick={handleReject} loading={actionLoading}>
                      {t('rejectQuote')}
                    </Button>
                    <Button
                      type="primary"
                      onClick={handleSend}
                      loading={actionLoading}
                      style={{ background: '#334E3B', borderColor: '#334E3B' }}
                    >
                      {t('sendQuote')}
                    </Button>
                  </div>
                )}
              </div>
            </Card>

            <Row gutter={[16, 16]}>
              <Col xs={24} lg={16}>
                <Card title={t('items')}>
                  <Table<QuoteItem>
                    columns={itemColumns}
                    dataSource={quote.draft_order?.items || []}
                    rowKey={(item) => item.id ?? item.title}
                    pagination={false}
                  />
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                    <div style={{ width: 280, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Text type="secondary">{t('subtotal')}</Text>
                        <Text>{formatAmount(quote.draft_order?.subtotal, currency)}</Text>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Text type="secondary">{t('tax')}</Text>
                        <Text>{formatAmount(quote.draft_order?.tax_total, currency)}</Text>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Text strong style={{ color: '#334E3B' }}>
                          {t('total')}
                        </Text>
                        <Text strong style={{ color: '#334E3B' }}>
                          {formatAmount(quote.draft_order?.total, currency)}
                        </Text>
                      </div>
                    </div>
                  </div>
                </Card>

                <Card title={t('messages')} style={{ marginTop: 16 }}>
                  {sortedMessages.length === 0 ? (
                    <Text type="secondary">{t('noMessages')}</Text>
                  ) : (
                    <Timeline
                      items={sortedMessages.map((msg) => ({
                        color: msg.customer ? 'blue' : 'green',
                        children: (
                          <div>
                            <Text strong>
                              {msg.customer
                                ? `${msg.customer.first_name} ${msg.customer.last_name}`
                                : quote.service_provider?.name || t('supplier')}
                            </Text>{' '}
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {new Date(msg.created_at).toLocaleString()}
                            </Text>
                            <div>{msg.text}</div>
                          </div>
                        ),
                      }))}
                    />
                  )}
                  <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                    <Input.TextArea
                      rows={2}
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      placeholder={t('messagePlaceholder')}
                    />
                    <Button
                      type="primary"
                      icon={<SendOutlined />}
                      onClick={handleSendMessage}
                      loading={sendingMessage}
                      disabled={!messageText.trim()}
                      style={{ background: '#334E3B', borderColor: '#334E3B' }}
                    >
                      {t('sendMessage')}
                    </Button>
                  </div>
                </Card>
              </Col>

              <Col xs={24} lg={8}>
                <Card title={t('customer')}>
                  <Descriptions column={1}>
                    <Descriptions.Item label={t('name')}>
                      {quote.customer
                        ? `${quote.customer.first_name} ${quote.customer.last_name}`
                        : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('email')}>
                      {quote.customer?.email || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('geoLocation')}>
                      {quote.geo_location?.name || '-'}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Spin>
    </div>
  );
};

export default RequestDetail;
